import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { profileActions } from '@figure/frontlib-onboarding-redux';
import PersonalLoanStateMachine from 'state-machines/PersonalLoanStateMachine';
import { personalLoanActions } from 'actions';
import { useRedirect } from 'shared/hooks';
import { isUuid } from 'shared/utils';

/**
 * Maps the state to the appropriate route.
 */
const stateToRoute = (state, appId) => {
  const { State } = PersonalLoanStateMachine;

  const base = `/applications/${appId}`;
  switch (state) {
    case State.START:
      return `${base}/start`;
    case State.OFFER_SELECTION:
    case State.OFFER_RESELECT:
      return `${base}/offer-select`;
    case State.ADDITIONAL_INFO:
      return `${base}/additional-info`;
    case State.ID_UPLOAD:
      return `${base}/id-upload`;
    case State.MANUAL_PAYSTUB_UPLOAD:
      return `${base}/income/upload/paystub`;
    case State.INCOME_VERIFICATION:
      return `${base}/income/review`;
    case State.SET_UP_DISBURSEMENT:
      return `${base}/bank/disbursement`;
    case State.EMPLOYER_VERIFICATION:
      return `${base}/employer-verification`;
    case State.SET_UP_AUTOPAY:
      return `${base}/bank/autopay`;
    case State.REVIEW_DOCS:
      return `${base}/signing/intro`;
    case State.TALK_TO_CSA:
      return `${base}/funding/intro`;
    case State.COMPLETE:
      return `${base}/complete`;
    // Holding Screens
    case State.APP_REVIEW:
      return `${base}/review-app/app-review`;
    // Error States
    case State.SSN_CREDIT:
      return `${base}/credit/social`;
    case State.ERROR_CREDIT:
      return `${base}/credit/error`;
    case State.INCORRECT_PIN:
      return `${base}/review-app/incorrect-pin`;
    case State.NO_CREDIT:
      return `${base}/review-app/no-credit`;
    case State.DELINQUENCY:
      return `${base}/review-app/delinquency`;
    case State.CREDIT_SCORE_DECLINE:
      return `${base}/decline/credit-score`;
    case State.DTI_DECLINE:
      return `${base}/decline/income`;
    case State.FPAY_CS_DECLINE:
      return `${base}/decline/fpay`;
    case State.FPAY_FIONA_DECLINE:
      return `${base}/decline/fiona`;
    case State.DECLINE:
      return `${base}/decline`;
    default:
      return null;
  }
};

/**
 * Hook to run the personal loan finite-state-machine.
 *
 * @returns {Function} Reference to a function to run the state machine.
 */
const usePersonalLoanRouteFsm = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const location = useLocation();
  const { redirectToDashboard } = useRedirect();

  let uuid = location?.pathname.split('/')?.[2] ?? undefined;
  uuid = isUuid(uuid) ? uuid : undefined;

  /**
   * Fetches the data needed to run the personal loan state machine.
   *
   * @param {String} appId Application UUID.
   * @returns {Promise} Object containing profile, personalLoan.
   */
  const fetchData = useCallback(
    async (appId) => {
      // Fetch data for router
      const [profile, personalLoan = {}] = await Promise.all([
        dispatch(profileActions.getProfile()),
        dispatch(personalLoanActions.getPersonalLoan(appId)),
      ]);

      return { profile, personalLoan };
    },
    [dispatch]
  );

  /**
   * Fetches the data needed for the finite-state-machine, runs the finite state machine, and transitions to the appropriate URL path
   * based on the current state.
   *
   * @todo Currently this makes 3-4 network requests.  Optimize router for fields passed in.
   */
  const runRouter = useCallback(
    async (appId = uuid) => {
      const { profile, personalLoan } = await fetchData(appId);
      const state = PersonalLoanStateMachine.run(profile, personalLoan);
      const route = stateToRoute(state, appId);

      if (!route) {
        redirectToDashboard();
        return;
      }

      try {
        history.push(route);
      } catch (e) {
        console.error(e);
        redirectToDashboard();
      }
    },
    [fetchData, uuid, history]
  );

  return runRouter;
};

export default usePersonalLoanRouteFsm;
